import { Box, Grid, Group, Stack, Text } from '@mantine/core'
import { IconAlertTriangleFilled } from '@tabler/icons-react'
import { FormattedMessage } from 'react-intl'
import {
  FeatureKey,
  FeatureUsage,
  FeatureUsageSummary
} from '@/types/subscription'
import { formatSize } from '@/utils/file'
import { UsageProgressBar } from '../UsageProgressBar/UsageProgressBar'
import { SIZE_UNIT_MAP } from '../usageConfig'
import { isUsageNearLimit } from '../usageUtils'

const FEATURE_LIST: FeatureKey[] = [
  'number_of_users',
  'number_of_apps',
  'user_ml_models',
  'number_of_streams',
  'model_trainings',
  'model_test_data',
  'event_storage',
  'dataset_storage',
  'vlm_agents'
]

// Features that should display a warning when usage exceeds the limit
// No warning needed for features like number of apps
const STORAGE_FEATURES: FeatureKey[] = [
  'event_storage',
  'dataset_storage',
  'model_test_data'
]

type TranslationKeyMap = Partial<
  Record<FeatureKey, { name: string; description: string }>
>

const TRANSLATION_KEYS: TranslationKeyMap = {
  number_of_streams: {
    name: 'subscriptions.usage.deployedCameras',
    description: 'subscriptions.usage.deployedCameras.description'
  },
  number_of_users: {
    name: 'subscriptions.usage.users',
    description: 'subscriptions.usage.users.description'
  },
  number_of_apps: {
    name: 'subscriptions.usage.apps',
    description: 'subscriptions.usage.apps.description'
  },
  model_trainings: {
    name: 'subscriptions.usage.modelTrainings',
    description: 'subscriptions.usage.modelTrainings.description'
  },
  model_test_data: {
    name: 'subscriptions.usage.modelTestData',
    description: 'subscriptions.usage.modelTestData.description'
  },
  user_ml_models: {
    name: 'subscriptions.usage.userModels',
    description: 'subscriptions.usage.userModels.description'
  },
  event_storage: {
    name: 'subscriptions.usage.eventStorage',
    description: 'subscriptions.usage.eventStorage.description'
  },
  dataset_storage: {
    name: 'subscriptions.usage.datasetStorage',
    description: 'subscriptions.usage.datasetStorage.description'
  },
  vlm_agents: {
    name: 'subscriptions.usage.vlmStreams',
    description: 'subscriptions.usage.vlmStreams.description'
  }
}

type FeatureUsageItemProps = FeatureUsage & {
  feature: FeatureKey
}

const FeatureUsageItem = ({
  feature,
  used,
  limit,
  unit,
  period,
  isUnlimited
}: FeatureUsageItemProps) => {
  const usagePercent = isUnlimited || limit === 0 ? 0 : (used / limit) * 100

  const isNearLimit =
    !isUnlimited &&
    STORAGE_FEATURES.includes(feature) &&
    isUsageNearLimit(used, limit)

  return (
    <Box key={feature}>
      <Group justify="space-between" mb={4}>
        <Text size="sm" fw={600}>
          <FormattedMessage id={TRANSLATION_KEYS[feature]?.name} />
        </Text>

        {isNearLimit && (
          <IconAlertTriangleFilled
            color="var(--mantine-color-yellow-6)"
            size={16}
          />
        )}
      </Group>

      <UsageProgressBar size="lg" isFull={isNearLimit} value={usagePercent} />

      <Grid mt={4}>
        <Grid.Col span={8}>
          <Text size="xs" c="dimmed">
            <FormattedMessage id={TRANSLATION_KEYS[feature]?.description} />
          </Text>
        </Grid.Col>

        <Grid.Col span={4}>
          <Text size="xs" fw={600} ta="right" mt={4}>
            {isUnlimited ? (
              <FormattedMessage id="subscriptions.usage.unlimited" />
            ) : (
              <>
                <FormattedMessage
                  id="subscriptions.usage.withTotal"
                  values={{
                    count:
                      unit === 'count'
                        ? used
                        : formatSize(used > limit ? limit : used, unit, false),
                    total: limit.toFixed(0),
                    unit: unit === 'count' ? '' : SIZE_UNIT_MAP[unit] || ''
                  }}
                />
                {period === 'monthly' && ' '}
                {period === 'monthly' && (
                  <FormattedMessage id="subscriptions.usage.thisMonth" />
                )}
              </>
            )}
          </Text>
        </Grid.Col>
      </Grid>
    </Box>
  )
}

type UsageSummaryProps = {
  featureUsageSummary: FeatureUsageSummary
}

export const UsageSummary = ({ featureUsageSummary }: UsageSummaryProps) => {
  return (
    <Stack gap="xl">
      {FEATURE_LIST.map((feature) => (
        <FeatureUsageItem
          key={feature}
          feature={feature}
          {...featureUsageSummary[feature]}
        />
      ))}
    </Stack>
  )
}
